import { useInvalidate } from '@refinedev/core'
import { useMutation } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

import { productCatalogDeleteProductMutation } from '@repo/sdk'

import { handleSdkError } from '../../providers/sdkErrorUtils'

interface UseDeleteProductReturn {
  deleteProduct: (productId: string) => void
  isDeleting: boolean
}

export function useDeleteProduct(onSuccess?: () => void): UseDeleteProductReturn {
  const invalidate = useInvalidate()
  const { message } = App.useApp()
  const { t } = useTranslation()

  const { mutate, isPending } = useMutation({
    ...productCatalogDeleteProductMutation(),
    onSuccess: async (_data, variables) => {
      message.success(t('pages.products.messages.deleteSuccess'))
      // refine keys product queries as ['data','default','product-list', …]:
      // a raw ['product-list'] key never matched, so the list stayed stale.
      await invalidate({
        resource: 'product-list',
        invalidates: ['list', 'detail'],
        id: variables.path?.productId,
      })
      onSuccess?.()
    },
    onError: (error) => {
      const { statusCode } = handleSdkError(error)
      if (statusCode === 403) {
        message.error(t('pages.products.messages.broadcastModifyRestricted'))
      } else {
        message.error(t('pages.products.messages.deleteFailed'))
      }
    },
  })

  const deleteProduct = (productId: string) => {
    mutate({ path: { productId } })
  }

  return { deleteProduct, isDeleting: isPending }
}
